/**
 * The canvas's `Capabilities` screen, redesigned.
 *
 * WHAT THE CONTENT ACTUALLY IS. The five items were rendered as one undivided
 * set in a six-column grid, three at span 2 then two at span 3, which made the
 * bottom pair half again as wide as the top three for no reason anyone could
 * name. But the split was groping at something real: the first three are things
 * the product DOES, and the last two are things it GUARANTEES. A capability and
 * a guarantee are different kinds of claim, so they now read as different
 * registers, and the layout difference finally means what it always looked like
 * it meant.
 *
 * WHAT CAME OUT, and why each was a false affordance rather than decoration:
 *
 *   The status dots. Five of them: cyan circle, amber square, green circle,
 *   cyan circle, teal square, on five coprime pulse rates. Amber and green are
 *   the vocabulary of health and warning, and nothing on this page has status,
 *   so the strongest signal in the card was pointing at nothing.
 *
 *   The 01-05 numerals. Numbering is the vocabulary of sequence; you do not do
 *   01 before 02 here. HowItWorks numbers 01-04 for four steps that genuinely
 *   are ordered, and running the same notation in both made the two sections
 *   scan alike. The notation now means one thing on the page.
 *
 *   Card 04's inversion. The previous comment called it "the only structural
 *   difference between them", which is the problem stated out loud: inversion
 *   is the loudest emphasis this system has, and spending it on the fourth item
 *   for no reason leaves emphasis meaning nothing anywhere.
 *
 *   "Learn more" five times, over five different destinations. A label that
 *   could sit under any card tells the visitor nothing about which one to
 *   press; each now names where it goes.
 *
 * Hierarchy is carried by material and type instead (Apple's HIG: material
 * weight encodes structure, and hierarchy is built from weight, size and
 * leading together rather than size alone). Capabilities sit on a filled
 * surface that lifts; guarantees are open, under a hairline, so they read as
 * the supporting register they are.
 *
 * The grid is narrow-first. The old one was a fixed `repeat(6, 1fr)` with no
 * media query at all, which put three span-2 cards side by side at roughly
 * 120px each on a phone.
 */

interface Capability {
  title: string;
  body: string;
  href: string;
  /** Names the destination. See the note above on "Learn more". */
  cta: string;
}

/** Things the product does. */
const CAPABILITIES: Capability[] = [
  {
    title: "AI-Powered Workforce",
    body: "Empower every team member with intelligent AI agents.",
    href: "#agents",
    cta: "See the agents",
  },
  {
    title: "Intelligent Workflows",
    body: "Automate, optimize, and scale your operations seamlessly.",
    href: "#how",
    cta: "See how it works",
  },
  {
    title: "Real-Time Intelligence",
    body: "Make smarter decisions with AI-driven insights and analytics.",
    href: "#platform",
    cta: "See the platform",
  },
];

/** Things the product guarantees. */
const GUARANTEES: Capability[] = [
  {
    title: "Secure. Scalable.",
    body: "Enterprise-grade security built to grow with your business.",
    href: "#trust",
    cta: "Read the security brief",
  },
  {
    title: "Built for Growth.",
    body: "From startups to enterprises, AI2Aim grows with your ambitions.",
    href: "#contact",
    cta: "Talk to us",
  },
];

/**
 * One card, in either register.
 *
 * The link stretches to cover the whole card through `.dc-cap-a::after`, so the
 * entire surface is pressable while the accessible tree still sees exactly one
 * link with one name. A card-shaped div with its own click handler would give
 * the same target and cost a tab stop and a role.
 */
function Card({ item, quiet }: { item: Capability; quiet?: boolean }) {
  return (
    <article
      data-rv=""
      data-rv-z=""
      className={quiet ? "dc-cap dc-cap-quiet" : "dc-cap dc-h-card"}
    >
      <h3 className="dc-cap-t">{item.title}</h3>
      <p className="dc-cap-b">{item.body}</p>
      <a className="dc-cap-a dc-h-link" href={item.href}>
        {item.cta}
        <span aria-hidden="true" className="dc-cap-arrow">
          &rarr;
        </span>
      </a>
    </article>
  );
}

export default function Capabilities() {
  return (
    <section
      id="capabilities"
      className="site-gutter"
      style={{
        /* Bottom trimmed from 110. The guarantees are an open register with no
           card box under them, so they end higher than the filled cards used
           to, and the band section below now opens with a 130px cross-over
           ramp. Holding 110 on top of both left the section reading
           bottom-heavy. */
        paddingTop: 118,
        paddingBottom: 84,
        position: "relative",
        overflow: "clip",
      }}
    >
      <div style={{ maxWidth: 1240, margin: "0 auto" }}>
        <div className="dc-cap-head" data-rv="">
          <h2 className="dc-cap-h2">Five capabilities, one platform</h2>
          <p className="dc-cap-lede">
            Your people, your workflows and your agents, running on the same
            engine.
          </p>
        </div>

        <div className="dc-cap-grid">
          {CAPABILITIES.map((c) => (
            <Card key={c.href} item={c} />
          ))}
        </div>

        <div className="dc-cap-grid dc-cap-grid-quiet">
          {GUARANTEES.map((c) => (
            <Card key={c.href} item={c} quiet />
          ))}
        </div>
      </div>
    </section>
  );
}
